import { Badge, Button, Col, Row, Tag, Typography, message, Modal, Select, Card, Statistic } from 'antd'
import styled from 'styled-components'

import React from 'react'
import { Link } from 'react-router-dom'

import { Store, StoresData, TempleData } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import { useAppContext } from '../../../../../components/AppContext'
import GenerateBatchModal from './GenerateBatchModal'

const { Title } = Typography
const { confirm } = Modal

interface Props {
  data: Store
  link: string
  onBatchGenerated: () => void
  storeList: StoresData[] | null | undefined
  templeListing: boolean
}

interface StoreStats {
  order_status_count: {
    booked: number
    live: number
  }
}

const Container = styled.div`
  padding: 10px;
  padding-left: 20px;
  display: flex;
  flex-direction: row;
  background-color: #fff;
  border: 1px solid #bfbfbf;
  .ant-card-body {
    flex-grow: 1;
  }
  flex-direction: row;
  &:hover {
    cursor: pointer;
  }
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
`

const ContentWrapper = styled(Title)`
  margin-bottom: 0 !important;
  margin-top: 0;
`

const SubTitleWrapper = styled.span`
  color: #434343;
`

const LabelRightAlign = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  height: 100%;
`

const Anchor = styled(Link)`
  margin-bottom: 20px;
  display: block;
`

const StatsCard = styled(Card)`
  margin-top: 16px;
  .ant-card-body {
    display: flex;
    justify-content: space-around;
    text-align: center;
  }
`

const StoreInfoSection = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
`

const TempleListItemInfo: React.FC<Props> = ({
  data: { id, name, city, store_type, service_type, total_active_batches },
  link,
  onBatchGenerated,
  storeList,
  templeListing
}) => {
  const [areBatchesGenerating, setAreBatchesGenerating] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [selectedStoreId, setSelectedStoreId] = React.useState<string | undefined>()
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState<boolean>(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = React.useState<boolean>(false)
  const [storeStats, setStoreStats] = React.useState<StoreStats | null>(null)
  const [loadingStats, setLoadingStats] = React.useState<boolean>(false)

  const { networkInstance, monetisationRoles } = useAppContext()
  const { monetisationOfferingsApi } = networkInstance.clientWithHeaders

  const generateBatch = async (storeId: string) => {
    setAreBatchesGenerating(true)
    try {
      await monetisationOfferingsApi.batchApi.generateBatches({
        store_id: storeId,
        user_email: monetisationRoles?.email || '',
      })
      message.success({
        content: 'Batches generated successfully',
        duration: 3,
      })
      onBatchGenerated()
    } catch (error) {
      message.error({
        content: 'Error while generating batch',
        duration: 3,
      })
    } finally {
      setAreBatchesGenerating(false)
    }
  }

  const fetchStoreStats = async (storeId: string) => {
    setLoadingStats(true)
    try {
      const response = await monetisationOfferingsApi.batchApi.showStats(storeId)
      setStoreStats(response?.data?.data)
      setIsStatsModalOpen(true)
    } catch (error) {
      message.error({
        content: 'Error fetching store statistics',
        duration: 3,
      })
    } finally {
      setLoadingStats(false)
    }
  }

  const handleClickGenerate = () => {
    if (templeListing) {
      setIsModalOpen(true)
    } else {
      confirm({
        title: 'Are you sure you want to generate batches?',
        content: `This will create new batches for the temple "${name}".`,
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => generateBatch(id),
      })
    }
  }

  const handleViewClick = (e: React.MouseEvent) => {
    e.preventDefault()
    fetchStoreStats(id)
  }

  const RowContainer = (
    <Container>
      <Row gutter={[16, 16]} align="middle" style={{ width: '100%' }}>
        <Col flex="300px">
          <InfoWrapper>
            <ContentWrapper level={5}>{name}</ContentWrapper>
            {<SubTitleWrapper>{city}</SubTitleWrapper>}
          </InfoWrapper>
        </Col>
        <Col flex="150px">
          <InfoWrapper>
            <ContentWrapper level={5}>
              {total_active_batches}
            </ContentWrapper>
            <SubTitleWrapper>Active Batches</SubTitleWrapper>
          </InfoWrapper>
        </Col>
        <Col flex="150px">
          <InfoWrapper>
            <Tag color='blue'>{service_type}</Tag>
          </InfoWrapper>
        </Col>
        <Col flex="auto">
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', alignItems: 'center' }}>
            <Button type='text' style={{ whiteSpace: 'nowrap' }}>View Batches &rarr;</Button>

            {service_type === 'offerings' && (
              <>
                <Button
                  type='primary'
                  loading={areBatchesGenerating}
                  ghost
                  onClick={(e) => {
                    e.preventDefault()
                    handleClickGenerate()
                  }}
                  size="small"
                >
                  Generate Batch
                </Button>
                {!templeListing && (
                  <Button
                    type='primary'
                    ghost
                    onClick={handleViewClick}
                    loading={loadingStats}
                    size="small"
                  >
                    Order Stat
                  </Button>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  )

  return (
    <>
      <Anchor to={link} key={link}>
        {store_type === 'mixed' ? (
          <Badge.Ribbon color='orange' text='Mixed Store'>
            {RowContainer}
          </Badge.Ribbon>
        ) : (
          RowContainer
        )}
      </Anchor>

      {templeListing && (
        <GenerateBatchModal
          visible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          storeList={storeList}
          onBatchGenerated={onBatchGenerated}
          generateBatchFn={generateBatch}
          loading={areBatchesGenerating}
        />
      )}

      <Modal
        title="Store Statistics"
        visible={isStatsModalOpen}
        onCancel={() => setIsStatsModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsStatsModalOpen(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <StoreInfoSection>
          <Typography.Title level={4}>{name}</Typography.Title>
          <Typography.Text>Store ID: {id}</Typography.Text>
          <br />
          <Typography.Text>Location: {city}</Typography.Text>
        </StoreInfoSection>

        <Typography.Title level={5}>Order Status</Typography.Title>
        <StatsCard>
          <Statistic
            title="Booked Orders"
            value={storeStats?.order_status_count?.booked || 0}
            valueStyle={{ color: '#1890ff' }}
          />
          <Statistic
            title="Live Orders"
            value={storeStats?.order_status_count?.live || 0}
            valueStyle={{ color: '#3f8600' }}
          />
        </StatsCard>
      </Modal>
    </>
  )
}

export default TempleListItemInfo